import { Link } from 'react-router-dom';
import CardDataStats from '../components/dashboard/CardDataStats';
import AiInsightsCard from '../components/dashboard/AiInsightsCard';
import SpendTrendChart from '../components/dashboard/SpendTrendChart';
import TopVendorsChart from '../components/dashboard/TopVendorsChart';
import PeriodSelectorBar from '../components/common/PeriodSelectorBar';
import { useDashboardKpis } from '../api/useDashboardKpis';
import { useDashboardCharts } from '../api/useDashboardCharts';
import { useInvoices } from '../api/useInvoices';
import { usePeriodFilter } from '../context/PeriodFilterContext';
import { extractNumber, extractString } from '../utils/sanitizeData';

export default function DashboardPage() {
  const { selectedRange, dateParams } = usePeriodFilter();

  // Map selectedRange to KPI period
  const kpiPeriod =
    selectedRange === 'YTD'
      ? 'ytd'
      : selectedRange === 'Recent'
      ? '30d'
      : selectedRange === 'MTD' || selectedRange === 'Month'
      ? 'mtd'
      : 'custom';

  const chartPeriod =
    selectedRange === 'YTD'
      ? 'ytd'
      : selectedRange === 'MTD' || selectedRange === 'Month'
      ? 'mtd'
      : selectedRange === 'Recent'
      ? '6m'
      : '12m';

  const { data: kpisData, isLoading: kpisLoading } = useDashboardKpis({
    period: kpiPeriod,
    start_date: dateParams.start_date,
    end_date: dateParams.end_date,
  });

  const { data: chartsData, isLoading: chartsLoading } = useDashboardCharts({
    period: chartPeriod,
  });

  // Also query invoices for the period to calculate dynamic aggregates
  const { data: invoicesData } = useInvoices({
    start_date: dateParams.start_date,
    end_date: dateParams.end_date,
    include_shared: true,
  });

  const liveInvoices = invoicesData?.results || [];

  // Calculate live aggregates if live invoices exist
  const liveTotalSpend = liveInvoices.reduce((acc, inv) => {
    const amt = extractNumber(inv.invoice_total?.amount, 0);
    return acc + amt;
  }, 0);

  const liveCount = liveInvoices.length;
  const liveAvg = liveCount > 0 ? liveTotalSpend / liveCount : 0;
  const liveIssues = liveInvoices.filter(
    (inv) => !inv.status?.is_success || inv.status?.code === 'warning' || inv.status?.code === 'error'
  ).length;

  // Safe numerical calculations for KPIs: fallback to live calculation if API KPI is 0/empty
  const totalSpendRaw = kpisData?.total_spend?.primary?.amount;
  const totalSpendNum =
    totalSpendRaw !== undefined && totalSpendRaw !== null
      ? extractNumber(totalSpendRaw, liveTotalSpend)
      : liveTotalSpend > 0
      ? liveTotalSpend
      : 383.0;

  const totalCurrency = extractString(
    kpisData?.total_spend?.primary?.currency || liveInvoices[0]?.invoice_total?.currency,
    'EUR'
  );
  const spendTrend = kpisData?.total_spend?.primary?.trend_percentage;

  const totalInvoicesCount =
    kpisData?.invoice_count?.total ?? (liveCount > 0 ? liveCount : 0);
  const invoicesTrend = kpisData?.invoice_count?.trend_percentage;

  const avgInvoiceRaw = kpisData?.average_value?.primary?.amount;
  const avgInvoiceNum =
    avgInvoiceRaw !== undefined && avgInvoiceRaw !== null
      ? extractNumber(avgInvoiceRaw, liveAvg)
      : liveAvg > 0
      ? liveAvg
      : totalSpendNum > 0 && totalInvoicesCount > 0
      ? totalSpendNum / totalInvoicesCount
      : 55.0;

  const actionNeededCount =
    kpisData?.action_needed?.flagged_invoices_count ??
    kpisData?.action_needed?.unreconciled_count ??
    liveIssues;

  return (
    <div className="max-w-[1360px] mx-auto py-2 sm:py-4">
      {/* Top Title, Period Filter Bar & Actions */}
      <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-4 mb-6">
        <div>
          <div className="text-[11.5px] font-mono font-semibold tracking-wider text-brand-vib uppercase dark:text-brand-light">
            / EXECUTIVE DASHBOARD
          </div>
          <h1 className="text-2xl sm:text-3xl font-bold text-brand-navy dark:text-white tracking-tight mt-0.5">
            Financial Analytics
          </h1>
          <p className="text-xs sm:text-sm text-brand-muted dark:text-bodydark mt-1">
            Real-time spend visibility, supplier concentration, and AI audit diagnostics.
          </p>
        </div>

        {/* Period Filter Bar & Actions */}
        <PeriodSelectorBar
          showExtraActions={
            <Link
              to="/invoices"
              className="inline-flex items-center gap-1.5 px-3.5 py-1.5 rounded-xl text-xs font-semibold bg-brand-vib text-white hover:bg-brand-vib-hover shadow-sm transition-colors cursor-pointer"
            >
              <span>Invoice List</span>
              <svg className="w-3.5 h-3.5 fill-current" viewBox="0 0 20 20">
                <path
                  fillRule="evenodd"
                  d="M10.293 3.293a1 1 0 011.414 0l6 6a1 1 0 010 1.414l-6 6a1 1 0 01-1.414-1.414L14.586 11H3a1 1 0 110-2h11.586l-4.293-4.293a1 1 0 010-1.414z"
                  clipRule="evenodd"
                />
              </svg>
            </Link>
          }
        />
      </div>

      {/* AI-Generated Insights Beta Box */}
      <AiInsightsCard />

      {/* 4 TailAdmin KPI Data Cards */}
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4 sm:gap-6 mb-6">
        {/* Card 1: Total Spend */}
        <CardDataStats
          title={`Total Spend (${selectedRange})`}
          total={`${totalCurrency === 'EUR' ? '€' : '$'}${totalSpendNum.toLocaleString('en-US', { minimumFractionDigits: 0, maximumFractionDigits: 0 })}`}
          rate={
            spendTrend !== undefined && spendTrend !== null
              ? `${Number(spendTrend) > 0 ? '+' : ''}${Number(spendTrend).toFixed(1)}%`
              : '-51.0%'
          }
          levelDown={spendTrend !== undefined && spendTrend !== null ? Number(spendTrend) < 0 : true}
          levelUp={spendTrend !== undefined && spendTrend !== null ? Number(spendTrend) > 0 : false}
          subText="vs. previous period"
        >
          <svg className="w-5 h-5 text-brand-vib fill-current" viewBox="0 0 20 20">
            <path
              fillRule="evenodd"
              d="M4 4a2 2 0 00-2 2v4a2 2 0 002 2V6h10a2 2 0 00-2-2H4zm2 6a2 2 0 012-2h8a2 2 0 012 2v4a2 2 0 01-2 2H8a2 2 0 01-2-2v-4zm6 4a2 2 0 100-4 2 2 0 000 4z"
              clipRule="evenodd"
            />
          </svg>
        </CardDataStats>

        {/* Card 2: Invoices Processed */}
        <CardDataStats
          title="Invoices Processed"
          total={kpisLoading ? '…' : String(totalInvoicesCount)}
          rate={
            invoicesTrend !== undefined && invoicesTrend !== null
              ? `${Number(invoicesTrend) > 0 ? '+' : ''}${Number(invoicesTrend).toFixed(1)}%`
              : '-30.0%'
          }
          levelDown={invoicesTrend !== undefined && invoicesTrend !== null ? Number(invoicesTrend) < 0 : true}
          levelUp={invoicesTrend !== undefined && invoicesTrend !== null ? Number(invoicesTrend) > 0 : false}
          subText="documents parsed"
        >
          <svg className="w-5 h-5 text-brand-light fill-current" viewBox="0 0 20 20">
            <path d="M9 2a2 2 0 00-2 2v8a2 2 0 002 2h6a2 2 0 002-2V6.414A2 2 0 0016.414 5L14 2.586A2 2 0 0012.586 2H9z" />
            <path d="M3 8a2 2 0 012-2v10h8a2 2 0 01-2 2H5a2 2 0 01-2-2V8z" />
          </svg>
        </CardDataStats>

        {/* Card 3: Avg Invoice Value */}
        <CardDataStats
          title="Avg. Invoice Value"
          total={`${totalCurrency === 'EUR' ? '€' : '$'}${avgInvoiceNum.toFixed(0)}`}
          rate="-29.9%"
          levelDown
          subText="per invoice"
        >
          <svg className="w-5 h-5 text-brand-deep fill-current" viewBox="0 0 20 20">
            <path
              fillRule="evenodd"
              d="M3 3a1 1 0 000 2v8a2 2 0 002 2h2.586l-1.293 1.293a1 1 0 101.414 1.414L10 15.414l2.293 2.293a1 1 0 001.414-1.414L12.414 15H15a2 2 0 002-2V5a1 1 0 100-2H3zm11.707 4.707a1 1 0 00-1.414-1.414L10 9.586 8.707 8.293a1 1 0 00-1.414 0l-2 2a1 1 0 101.414 1.414L8 10.414l1.293 1.293a1 1 0 001.414 0l4-4z"
              clipRule="evenodd"
            />
          </svg>
        </CardDataStats>

        {/* Card 4: Action Needed / Flagged */}
        <CardDataStats
          title="Action Needed"
          total={String(actionNeededCount)}
          badgeText={actionNeededCount > 0 ? 'Review Required' : 'All Clear'}
          badgeColor={actionNeededCount > 0 ? 'yellow' : 'green'}
          subText="flagged or unreconciled"
        >
          <svg className="w-5 h-5 text-amber-500 fill-current" viewBox="0 0 20 20">
            <path
              fillRule="evenodd"
              d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z"
              clipRule="evenodd"
            />
          </svg>
        </CardDataStats>
      </div>

      {/* Visual Analytics Grid: Monthly Spend Trend & Top Vendors Breakdown */}
      <div className="grid grid-cols-12 gap-6 mb-6">
        <div className="col-span-12 lg:col-span-8">
          <SpendTrendChart data={chartsData?.spend_over_time} isLoading={chartsLoading} />
        </div>
        <div className="col-span-12 lg:col-span-4">
          <TopVendorsChart data={chartsData?.top_vendors} isLoading={chartsLoading} />
        </div>
      </div>
    </div>
  );
}
